import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    ShieldCheck,
    Activity,
    Stethoscope,
    Lock,
    ArrowRight,
    CheckCircle2,
    AlertCircle,
    Loader2,
    User as UserIcon,
    ClipboardCheck
} from 'lucide-react';
import Skeleton from '../../Components/common/Skeleton';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const BASE_API = API_URL.endsWith('/api') ? API_URL : `${API_URL}/api`;

const SurgeryVerify = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const role = searchParams.get('role') || 'surgeon'; // surgeon or anesthesiologist
    const type = searchParams.get('type') || 'start'; // start or completion

    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [success, setSuccess] = useState(false);
    const [lead, setLead] = useState(null);

    useEffect(() => {
        const fetchLead = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`${BASE_API}/leads/public/${id}`);
                setLead(response.data.data);
            } catch (error) {
                console.error('Error fetching lead:', error);
                toast.error('Could not load case details.');
            } finally {
                setLoading(false);
            }
        };
        fetchLead();
    }, [id]);

    const handleVerify = async (e) => {
        e.preventDefault();
        if (otp.length < 6) {
            toast.error('Please enter a valid 6-digit code.');
            return;
        }

        try {
            setVerifying(true);
            const endpoint = type === 'completion' ? 'verify-completion-otp' : 'verify-surgery-otp';
            const response = await axios.post(`${BASE_API}/leads/public/${endpoint}/${id}`, {
                otp,
                role
            });

            if (response.data.success) {
                setSuccess(true);
                toast.success(type === 'completion' ? 'Case completed successfully!' : 'Surgery started successfully!');
            }
        } catch (error) {
            console.error('Verification error:', error);
            const msg = error.response?.data?.message || 'Verification failed. Please check the code.';
            toast.error(msg, { duration: 5000 });
            if (msg.toLowerCase().includes('invalid')) {
                toast("Tip: Ensure you are using the 'Work Completion' code, not the 'Surgery Start' code.", { icon: '💡', duration: 6000 });
            }
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a0f1d] flex flex-col items-center justify-center p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/5 blur-[120px] rounded-full" />
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-600/5 blur-[120px] rounded-full" />

                <div className="max-w-md w-full space-y-8 relative z-10">
                    <div className="text-center space-y-4">
                        <Skeleton className="w-16 h-16 rounded-2xl mx-auto bg-white/5" />
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-48 mx-auto bg-white/5" />
                            <Skeleton className="h-4 w-32 mx-auto bg-white/5 opacity-50" />
                        </div>
                    </div>

                    <div className="bg-[#161b2c] border border-white/5 rounded-3xl p-6 space-y-6">
                        <div className="space-y-3">
                            <Skeleton className="h-3 w-24 bg-white/5 opacity-30" />
                            <Skeleton className="h-6 w-40 bg-white/5" />
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/5">
                            <div className="space-y-2">
                                <Skeleton className="h-2 w-16 bg-white/5 opacity-30" />
                                <Skeleton className="h-4 w-24 bg-white/5" />
                            </div>
                            <div className="space-y-2">
                                <Skeleton className="h-2 w-16 bg-white/5 opacity-30" />
                                <Skeleton className="h-4 w-24 bg-white/5" />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <Skeleton className="h-14 w-full rounded-2xl bg-white/5" />
                        <Skeleton className="h-16 w-full rounded-2xl bg-white/5" />
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-[#0a0f1d] flex items-center justify-center p-6">
                <div className={`max-w-md w-full bg-[#161b2c] border rounded-3xl p-8 text-center space-y-6 shadow-2xl animate-in fade-in zoom-in duration-500 ${type === 'completion' ? 'border-blue-500/30 shadow-blue-500/10' : 'border-emerald-500/30 shadow-emerald-500/10'}`}>
                    <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto ${type === 'completion' ? 'bg-blue-500/20' : 'bg-emerald-500/20'}`}>
                        {type === 'completion' ? <CheckCircle2 className="w-12 h-12 text-blue-400" /> : <Activity className="w-12 h-12 text-emerald-400 animate-pulse" />}
                    </div>
                    <h1 className="text-3xl font-black text-white tracking-tight uppercase">
                        {type === 'completion' ? 'Case Completed' : 'LIVE SURGERY'}
                    </h1>
                    <p className={`${type === 'completion' ? 'text-blue-400 bg-blue-400/10 border-blue-400/20' : 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'} font-bold py-2 rounded-full border`}>
                        VERIFICATION SUCCESSFUL
                    </p>
                    <div className="space-y-4 text-emerald-100/70 text-sm">
                        <p>The patient <strong>{lead?.patientName}</strong>'s {type === 'completion' ? 'surgery record has been finalized.' : 'has been moved to the Live Surgery stage.'}</p>
                        <p>{type === 'completion' ? 'Thank you for your dedicated clinical service.' : 'The OT session is now active in the CRM system.'}</p>
                    </div>
                    <div className="pt-4">
                        <div className={`flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest ${type === 'completion' ? 'text-blue-500' : 'text-emerald-500'}`}>
                            {type === 'completion' ? 'Clinical Record Sealed' : <><span className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" />Live Monitoring Active</>}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0f1d] flex flex-col items-center justify-center p-6 relative overflow-hidden font-sans">
            {/* Background Decorations */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 blur-[120px] rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-600/10 blur-[120px] rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="max-w-md w-full space-y-8 relative z-10">
                {/* Header */}
                <div className="text-center space-y-2">
                    <div className="flex justify-center mb-4">
                        <div className="p-3 bg-white/5 rounded-2xl border border-white/10 shadow-inner">
                            <ShieldCheck className={`w-10 h-10 ${type === 'completion' ? 'text-blue-400' : 'text-emerald-400'}`} />
                        </div>
                    </div>
                    <h1 className="text-3xl font-black text-white tracking-tighter uppercase italic">
                        Surgery <span className={type === 'completion' ? 'text-blue-500' : 'text-emerald-500'}>Protocol</span>
                    </h1>
                    <p className="text-slate-400 font-medium">{type === 'completion' ? 'Verify Case Completion' : 'Verify OT Commencement'}</p>
                </div>

                {/* Case Card */}
                <div className="bg-[#161b2c] border border-white/5 rounded-3xl p-6 shadow-2xl relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <Stethoscope className="w-12 h-12 text-white" />
                    </div>

                    <div className="space-y-4">
                        <div>
                            <p className="text-[10px] items-center flex gap-1 uppercase tracking-[0.2em] text-slate-500 font-bold mb-1">
                                <Activity className="w-3 h-3" /> Patient Identity
                            </p>
                            <p className="text-xl font-bold text-white tracking-tight">{lead?.patientName || 'Loading...'}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-white/5">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Procedure</p>
                                <p className="text-sm font-semibold text-slate-200">{lead?.treatmentRequired || lead?.disease || 'Surgical Case'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Clinic Role</p>
                                <p className={`text-sm font-bold uppercase italic ${type === 'completion' ? 'text-blue-400' : 'text-emerald-400'}`}>
                                    {role === 'surgeon' ? 'Lead Surgeon' : 'anesthesiologist'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* OTP Form */}
                <form onSubmit={handleVerify} className="space-y-6">
                    <div className="space-y-4">
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                                <Lock className="w-5 h-5" />
                            </div>
                            <input
                                type="text"
                                maxLength="6"
                                placeholder="ENTER 6-DIGIT VERIFICATION CODE"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
                                className={`w-full bg-[#161b2c] border-2 border-white/5 rounded-2xl py-4 pl-12 pr-4 text-white text-center text-lg font-black tracking-[0.5em] placeholder:text-slate-600 focus:outline-none focus:ring-4 transition-all ${type === 'completion' ? 'focus:border-blue-500/50 focus:ring-blue-500/10' : 'focus:border-emerald-500/50 focus:ring-emerald-500/10'}`}
                                required
                            />
                        </div>
                        <p className="text-[10px] text-center text-slate-500 uppercase tracking-widest font-bold">
                            Enter the Completion OTP provided by the facility staff
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={verifying || otp.length < 6}
                        className={`w-full group relative overflow-hidden disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-black py-5 rounded-2xl transition-all shadow-xl active:scale-95 ${type === 'completion' ? 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/20' : 'bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/20'}`}
                    >
                        {/* Premium Shimmer Overlay when verifying */}
                        {verifying && (
                            <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent z-10"></div>
                        )}

                        <div className="flex items-center justify-center gap-2 relative z-20">
                            {verifying ? (
                                <>
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                    <span className="tracking-tighter text-lg uppercase">Validating Protocol...</span>
                                </>
                            ) : (
                                <>
                                    <span className="tracking-tighter text-lg uppercase">{type === 'completion' ? 'Finalize Case Verification' : 'Verify & Start Surgery'}</span>
                                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </div>
                    </button>
                </form>

                {/* Footer Info */}
                <div className="text-center pt-4 border-t border-white/5">
                    <p className="text-[10px] text-slate-500 uppercase tracking-widest leading-relaxed">
                        Secure Multi-Party Verification Protocol<br />
                        DOXEZ Healthcare Systems &copy; {new Date().getFullYear()}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SurgeryVerify;
